import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { ImageLightbox, type LightboxImage } from "@/components/ai/chat/ImageLightbox";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useCallback, useMemo, useState, type FC } from "react";
import type { ImageAttachment } from "@/components/ai/prompt-input/state";

type ImageAttachmentStripProps = {
    images: ImageAttachment[];
    onRemove: (id: string) => void;
};

export const ImageAttachmentStrip: FC<ImageAttachmentStripProps> = ({ images, onRemove }) => {
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const isLightboxOpen = lightboxIndex >= 0;

    const lightboxImages: LightboxImage[] = useMemo(
        () => images.map((img) => ({ src: img.preview, alt: img.name })),
        [images],
    );

    const handleOpenLightbox = useCallback((index: number) => {
        setLightboxIndex(index);
    }, []);

    const handleCloseLightbox = useCallback((open: boolean) => {
        if (!open) setLightboxIndex(-1);
    }, []);

    const handleRemove = useCallback(
        (e: React.MouseEvent, id: string) => {
            e.stopPropagation();
            // Don't revoke the blob URL here — the lightbox may still be displaying it.
            // The parent (selectedImagesAtom setter) handles cleanup.
            onRemove(id);
        },
        [onRemove],
    );

    return (
        <>
            <div className="flex gap-2 overflow-x-auto px-1 py-1">
                {images.map((image, index) => (
                    <Tooltip key={image.id} content={image.name}>
                        <div className="group relative shrink-0">
                            <button
                                type="button"
                                onClick={() => handleOpenLightbox(index)}
                                className={cn(
                                    "block size-12 overflow-hidden rounded-md border border-border-subtle",
                                    "transition-opacity hover:opacity-80",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-link",
                                )}
                                aria-label={`Preview ${image.name}`}
                                data-tracking-id="image-attachment-preview"
                            >
                                <img src={image.preview} alt={image.name} className="size-full object-contain" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleRemove(e, image.id)}
                                className="absolute -right-1 -top-1 rounded-full bg-surface-pure text-content-muted opacity-0 transition-opacity hover:text-content-accent group-hover:opacity-100"
                                aria-label={`Remove ${image.name}`}
                                data-tracking-id="delete-image-context-prompt-input"
                            >
                                <IconCircleX className="size-4" />
                            </button>
                        </div>
                    </Tooltip>
                ))}
            </div>
            <ImageLightbox
                images={lightboxImages}
                currentIndex={isLightboxOpen ? lightboxIndex : 0}
                onIndexChange={setLightboxIndex}
                open={isLightboxOpen}
                onOpenChange={handleCloseLightbox}
            />
        </>
    );
};
